import { useEffect } from 'react';

interface Campaign {
  id: number;
  title: string;
  desc: string;
  oldPrice: string;
  newPrice: string;
  badge: string;
  badgeColor: string;
  image: string;
  tag: string;
  wide?: boolean;
}

interface Props {
  campaign: Campaign;
  onClose: () => void;
}

export default function CampaignModal({ campaign, onClose }: Props) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const scrollToContact = () => {
    onClose();
    setTimeout(() => {
      const el = document.getElementById('iletisim');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-3xl overflow-hidden border border-white/10 animate-[fadeInUp_0.3s_ease-out]"
        style={{ background: 'rgba(13, 17, 23, 0.98)', backdropFilter: 'blur(40px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image */}
        <div className="relative h-56 overflow-hidden">
          <img src={campaign.image} alt={campaign.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117] via-[#0d1117]/30 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div
            className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold"
            style={{ background: `${campaign.badgeColor}20`, border: `1px solid ${campaign.badgeColor}60`, color: campaign.badgeColor }}
          >
            {campaign.badge}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-white font-black text-2xl mb-1">{campaign.title}</h3>
              <span className="text-[#d4af37] text-xs font-bold uppercase tracking-widest">{campaign.tag}</span>
            </div>
            <div className="text-right">
              <div className="text-white/30 text-sm line-through">{campaign.oldPrice}</div>
              <div
                className="text-3xl font-black"
                style={{ background: 'linear-gradient(135deg, #d4af37, #f4d03f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
              >
                {campaign.newPrice}
              </div>
            </div>
          </div>

          <p className="text-white/60 text-sm leading-relaxed mb-6">{campaign.desc}</p>

          {/* Features */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {['Uzman Ekip', 'Kişisel Danışman', 'Garanti', 'Hızlı Sonuç'].map((f) => (
              <div key={f} className="flex items-center gap-2 text-white/60 text-sm">
                <i className="ri-check-line text-[#00d9ff] text-base"></i>
                {f}
              </div>
            ))}
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6 p-3 rounded-xl bg-[#00d9ff]/5 border border-[#00d9ff]/20">
            <i className="ri-information-line text-[#00d9ff]"></i>
            <span className="text-[#00d9ff] text-xs">Bir sonraki adım: Randevu oluşturun veya bilgi alın</span>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://wa.me/905001234567?text=Merhaba%2C%20kampanya%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-black font-bold text-sm flex items-center justify-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base"></i>
              WhatsApp&apos;tan Yaz
            </a>
            <button
              onClick={scrollToContact}
              className="flex-1 py-3.5 rounded-xl border border-[#00d9ff]/50 text-[#00d9ff] font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#00d9ff]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line text-base"></i>
              Randevu Al
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
